"use client";

import { useEffect, type RefObject } from "react";

/**
 * Scroll reveals — the `[data-fade]` / `[data-rise]` idiom from `globals.css`.
 *
 * This hook is only the trigger: it marks `[data-rise-on]` when a target enters the fold.
 * Curve, duration and the `prefers-reduced-motion` override live in `globals.css`, so a second
 * guard here would only add a path where content could stay invisible.
 */

/** Targets not revealed yet. `[data-rise]` is observed through its `[data-rise-wrap]` pair. */
export const REVEAL_SELECTOR =
  "[data-rise-wrap]:not([data-rise-on]), [data-fade]:not([data-rise-on])";

/**
 * The sweep runs on a ladder of delays, not once: blocks below the fold mount progressively,
 * and each pass picks up targets that did not exist yet.
 */
const RETRY_DELAYS_MS = [80, 400, 1200];

export type RevealTuning = {
  /** Fraction of the target that has to be visible. */
  threshold: number;
  /** Shrinks the trigger area; the negative value at the end fires inside the fold. */
  rootMargin: string;
};

/** Public screens: the numbers from the design reference. */
export const EDITORIAL_REVEAL: RevealTuning = {
  threshold: 0.3,
  rootMargin: "0px 0px -8% 0px",
};

/**
 * Host panel: a block taller than the viewport is the rule here, not the exception.
 *
 * With a trigger by **fraction of the target**, a 3000px block in an 800px viewport never
 * reaches 30% visible and would stay transparent forever. So the panel fires on any overlap,
 * and the 12% inset at the foot is what keeps the reveal happening *inside* the fold.
 */
export const PANEL_REVEAL: RevealTuning = {
  threshold: 0,
  rootMargin: "0px 0px -12% 0px",
};

/**
 * Observes the reveal targets under `rootRef`.
 *
 * @param restartKey restarts the sweep from scratch. Switching tabs swaps all the content under
 * the same root without unmounting the shell, and already revealed targets carry
 * `[data-rise-on]`, so restarting repeats no animation.
 */
export function useReveals(
  rootRef: RefObject<HTMLElement | null>,
  tuning: RevealTuning,
  restartKey?: string,
): void {
  const { threshold, rootMargin } = tuning;

  useEffect(() => {
    /** Who is already being observed. Kept out of the DOM so the markup stays clean. */
    const watched = new WeakSet<Element>();
    let intersections: IntersectionObserver | null = null;
    let mutations: MutationObserver | null = null;
    let frame: number | null = null;

    const ensureIntersections = () => {
      if (intersections !== null) return intersections;

      const created = new IntersectionObserver(
        (entries) => {
          for (const entry of entries) {
            if (!entry.isIntersecting) continue;

            entry.target.setAttribute("data-rise-on", "");
            // Once only: a reveal does not repeat, so stop paying for the target.
            created.unobserve(entry.target);
          }
        },
        { threshold, rootMargin },
      );
      intersections = created;

      return created;
    };

    const scan = () => {
      const root = rootRef.current;
      if (root === null) return;

      // The panel's content changes without a route change: statement lens, catalog facet, a
      // form entering edit. A target that mounts after the ladder of delays has to be
      // observed, or it is born transparent and stays that way.
      if (mutations === null) {
        mutations = new MutationObserver(queueScan);
        mutations.observe(root, { childList: true, subtree: true });
      }

      const targets = Array.from(root.querySelectorAll(REVEAL_SELECTOR)).filter(
        (target) => !watched.has(target),
      );
      if (targets.length === 0) return;

      const observer = ensureIntersections();

      for (const target of targets) {
        watched.add(target);
        observer.observe(target);
      }
    };

    // One sweep per frame. Filtering the catalog swaps hundreds of nodes at once, and the
    // alternative would be a `querySelectorAll` per mutation.
    function queueScan() {
      if (frame !== null) return;

      frame = requestAnimationFrame(() => {
        frame = null;
        scan();
      });
    }

    const timers = RETRY_DELAYS_MS.map((delay) => setTimeout(scan, delay));

    return () => {
      timers.forEach(clearTimeout);
      if (frame !== null) cancelAnimationFrame(frame);
      mutations?.disconnect();
      intersections?.disconnect();
    };
  }, [rootRef, threshold, rootMargin, restartKey]);
}
